import { useState, useRef, useEffect } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LayoutDashboard,
  UploadCloud,
  ListMusic,
  Fingerprint,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Bell,
  ShieldCheck,
  Shield,
  CheckCircle2,
  AlertTriangle,
  Info,
  Clock,
  Check,
  Loader2
} from 'lucide-react'
import { useAuthStore, isAdminUser } from '@/store/auth'
import { useProcessingSubscriptions } from '@/hooks/useProcessingSubscriptions'
import { authService } from '@/services/firebase.service'
import { cn } from '@/utils/cn'
import toast from 'react-hot-toast'

// Notification types
interface Notification {
  id: string
  type: 'success' | 'warning' | 'info'
  title: string
  message: string
  time: string
  read: boolean
  link?: string // Optional navigation link
}

const baseNavigation = [
  { name: 'Overview', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Nuova Analisi', href: '/upload', icon: UploadCloud },
  { name: 'Archivio Call', href: '/recordings', icon: ListMusic },
  { name: 'Voice ID', href: '/voice-library', icon: Fingerprint },
  { name: 'Impostazioni', href: '/settings', icon: Settings },
]

const adminNavigation = [
  { name: 'Admin Panel', href: '/admin', icon: Shield, adminOnly: true },
]

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([
    { id: '1', type: 'success', title: 'Trascrizione completata', message: 'Board Meeting Q4 è stato analizzato con successo.', time: '2 min fa', read: false, link: '/recordings' },
    { id: '2', type: 'info', title: 'Nuovo profilo vocale', message: 'Marco Rossi è stato aggiunto alla libreria.', time: '1 ora fa', read: false, link: '/voice-library' },
    { id: '3', type: 'warning', title: 'Limite minuti', message: "Hai utilizzato l'80% dei minuti mensili.", time: '3 ore fa', read: true, link: '/settings' },
    { id: '4', type: 'success', title: 'Export completato', message: 'Report settimanale generato in PDF.', time: '1 giorno fa', read: true, link: '/recordings' },
  ])

  const notificationRef = useRef<HTMLDivElement>(null)
  const processingRef = useRef<HTMLDivElement>(null)
  const { user, logout } = useAuthStore()
  const { processingCount, hasProcessing, processingFiles } = useProcessingSubscriptions()
  const [processingOpen, setProcessingOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false)
      }
      if (processingRef.current && !processingRef.current.contains(event.target as Node)) {
        setProcessingOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    try {
      await authService.logout()
      logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
      // Force logout from store anyway
      logout()
      navigate('/login')
    }
  }

  const unreadCount = notifications.filter(n => !n.read).length

  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(n =>
      n.id === id ? { ...n, read: true } : n
    ))
  }

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id)
    if (notification.link) {
      setNotificationsOpen(false)
      navigate(notification.link)
    }
  }

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    toast.success('Tutte le notifiche segnate come lette')
  }

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success': return <CheckCircle2 className="h-5 w-5 text-emerald-500" />
      case 'warning': return <AlertTriangle className="h-5 w-5 text-amber-500" />
      case 'info': return <Info className="h-5 w-5 text-blue-500" />
    }
  }

  // Header dinamico in base alla pagina
  const getPageTitle = () => {
    const path = location.pathname
    if (path.includes('dashboard')) return 'Executive Dashboard'
    if (path.includes('upload')) return 'Ingestione Dati'
    if (path.includes('recordings')) return 'Archivio Intelligence'
    if (path.includes('voice')) return 'Gestione Identità'
    if (path.includes('settings')) return 'Configurazione Workspace'
    if (path.includes('admin')) return 'Admin Panel'
    return 'NexScribe Enterprise'
  }

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile Backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-slate-900/80 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar Enterprise (Dark Navy) */}
      <aside className={cn(
        'fixed inset-y-0 left-0 z-50 w-72 bg-slate-900 text-white transform transition-transform duration-300 ease-in-out lg:static lg:translate-x-0 border-r border-slate-800 shadow-2xl',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex h-full flex-col">
          {/* Logo Area */}
          <div className="flex h-20 items-center gap-3 px-6 border-b border-slate-800 bg-slate-900">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-800 border border-slate-700">
              <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <div>
              <span className="text-xl font-bold tracking-tight text-white block">NexScribe</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Enterprise</span>
            </div>
            <button className="ml-auto lg:hidden text-slate-400" onClick={() => setSidebarOpen(false)}>
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-4 py-8">
            <p className="px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Main Menu</p>
            {baseNavigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-slate-800 text-white border border-slate-700'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  )
                }
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            ))}

            {/* Admin Section - Only for admin users */}
            {isAdminUser(user?.email) && (
              <>
                <p className="px-4 text-xs font-semibold text-red-500 uppercase tracking-wider mt-6 mb-4">Admin</p>
                {adminNavigation.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-all duration-200',
                        isActive
                          ? 'bg-red-900/50 text-red-200 border border-red-800'
                          : 'text-red-400 hover:bg-red-900/30 hover:text-red-200'
                      )
                    }
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                  </NavLink>
                ))}
              </>
            )}
          </nav>

          {/* User Profile Footer */}
          <div className="border-t border-slate-800 p-4 bg-slate-900">
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex w-full items-center gap-3 rounded-xl p-3 hover:bg-slate-800 transition-colors border border-transparent hover:border-slate-700"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 border border-slate-700 text-slate-200 font-semibold">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white">{user?.name || 'Utente'}</p>
                  <p className="text-xs text-slate-400 truncate max-w-[120px]">{user?.email}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-500" />
              </button>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute bottom-full left-0 right-0 mb-2 rounded-2xl glass-panel-dark p-1 shadow-2xl overflow-hidden z-[200]"
                  >
                    <button onClick={handleLogout} className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-red-400 hover:bg-red-950/30 hover:text-red-300 transition-colors">
                      <LogOut className="h-4 w-4" />
                      Disconnetti Sicurezza
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden ambient-bg relative">
        {/* Top Header - Frosted Glass iOS Style */}
        <header className="h-20 frosted-header flex items-center justify-between px-8 z-[100] sticky top-0">
          <div className="flex items-center gap-4">
            <button className="lg:hidden p-2 -ml-2 text-slate-600 glass-button" onClick={() => setSidebarOpen(true)}>
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-xl font-bold text-slate-800">{getPageTitle()}</h1>
          </div>

          <div className="flex items-center gap-4">
            {/* Processing Indicator */}
            {hasProcessing && (
              <div className="relative" ref={processingRef}>
                <button
                  onClick={() => setProcessingOpen(!processingOpen)}
                  className="relative flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors border border-amber-200"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{processingCount} in elaborazione</span>
                </button>

                {/* Processing Dropdown - Glass Style */}
                <AnimatePresence>
                  {processingOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-2 w-80 glass-dropdown overflow-hidden z-[200]"
                    >
                      <div className="px-4 py-3 border-b border-white/30 bg-white/50">
                        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
                          File in elaborazione
                        </h3>
                      </div>
                      <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                        {processingFiles
                          .filter((f) => f.status !== 'completed' && f.status !== 'error')
                          .map((file) => (
                            <div key={file.id} className="px-4 py-3">
                              <p className="text-sm font-medium text-slate-800 truncate">
                                {file.fileName}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <span
                                  className={cn(
                                    'text-xs px-2 py-0.5 rounded-full font-medium',
                                    file.status === 'uploading' && 'bg-blue-100 text-blue-700',
                                    file.status === 'processing' && 'bg-amber-100 text-amber-700',
                                    file.status === 'transcribing' && 'bg-purple-100 text-purple-700',
                                    file.status === 'analyzing' && 'bg-emerald-100 text-emerald-700'
                                  )}
                                >
                                  {file.status === 'uploading' && 'Caricamento...'}
                                  {file.status === 'processing' && 'In coda...'}
                                  {file.status === 'transcribing' && 'Whisper...'}
                                  {file.status === 'analyzing' && 'GPT-4...'}
                                </span>
                                {file.status === 'uploading' && (
                                  <span className="text-xs text-slate-500">
                                    {file.progress}%
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Notifications Bell */}
            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="relative p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 h-4 w-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold border-2 border-white">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown - Glass Style */}
              <AnimatePresence>
                {notificationsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 mt-2 w-96 glass-dropdown overflow-hidden z-[200]"
                  >
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-white/30 bg-white/50 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4 text-slate-500" />
                        <h3 className="font-semibold text-slate-800">Notifiche</h3>
                        {unreadCount > 0 && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                            {unreadCount} nuove
                          </span>
                        )}
                      </div>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                        >
                          <Check className="h-3 w-3" />
                          Segna tutte lette
                        </button>
                      )}
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-8 text-center">
                          <Bell className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                          <p className="text-sm text-slate-500">Nessuna notifica</p>
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <div
                            key={notification.id}
                            onClick={() => handleNotificationClick(notification)}
                            className={cn(
                              'px-4 py-3 flex gap-3 hover:bg-slate-50 transition-colors cursor-pointer border-b border-slate-100 last:border-0',
                              !notification.read && 'bg-blue-50/50'
                            )}
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className={cn(
                                  'text-sm',
                                  notification.read ? 'font-medium text-slate-700' : 'font-semibold text-slate-900'
                                )}>
                                  {notification.title}
                                </p>
                                {!notification.read && (
                                  <span className="flex-shrink-0 h-2 w-2 rounded-full bg-blue-500 mt-1.5" />
                                )}
                              </div>
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                {notification.message}
                              </p>
                              <div className="flex items-center gap-1 mt-1.5 text-xs text-slate-400">
                                <Clock className="h-3 w-3" />
                                {notification.time}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-3 border-t border-white/30 bg-white/50">
                      <button
                        onClick={() => {
                          navigate('/settings')
                          setNotificationsOpen(false)
                        }}
                        className="w-full text-center text-sm text-slate-600 hover:text-slate-900 font-medium"
                      >
                        Gestisci preferenze notifiche
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Page Content Injection */}
        <main className="flex-1 overflow-auto p-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
